import { CODON_TABLE, synonymousCodons } from "./codon-table";
import { cleanSequence } from "./sequence";

export interface CodonStat {
  codon: string;
  aa: string;
  count: number;
  /** occurrences per 1000 codons */
  per1000: number;
  /** fraction among synonymous codons for the same amino acid */
  fraction: number;
  /** relative synonymous codon usage */
  rscu: number;
}

export interface CodonAnalysis {
  total: number;
  stats: CodonStat[];
}

/** Count in-frame (frame 0) codons that appear in the standard genetic code. */
export function countCodons(input: string): Record<string, number> {
  const seq = cleanSequence(input).replace(/U/g, "T");
  const counts: Record<string, number> = {};
  for (let i = 0; i + 3 <= seq.length; i += 3) {
    const codon = seq.slice(i, i + 3);
    if (codon in CODON_TABLE) counts[codon] = (counts[codon] ?? 0) + 1;
  }
  return counts;
}

/**
 * Full codon-usage + RSCU analysis of a coding sequence. RSCU is the observed
 * codon count divided by the count expected if all synonymous codons for that
 * amino acid were used equally.
 */
export function analyzeCodons(input: string): CodonAnalysis {
  const counts = countCodons(input);
  const groups = synonymousCodons();
  const total = Object.values(counts).reduce((a, b) => a + b, 0);

  // Sum of counts per amino acid.
  const aaTotals: Record<string, number> = {};
  for (const [codon, aa] of Object.entries(CODON_TABLE)) {
    aaTotals[aa] = (aaTotals[aa] ?? 0) + (counts[codon] ?? 0);
  }

  const stats: CodonStat[] = [];
  for (const [codon, aa] of Object.entries(CODON_TABLE)) {
    const count = counts[codon] ?? 0;
    if (count === 0) continue;
    const aaTotal = aaTotals[aa];
    const nSyn = groups[aa].length;
    stats.push({
      codon,
      aa,
      count,
      per1000: total > 0 ? (count / total) * 1000 : 0,
      fraction: aaTotal > 0 ? count / aaTotal : 0,
      rscu: aaTotal > 0 ? (count * nSyn) / aaTotal : 0,
    });
  }

  stats.sort((a, b) =>
    a.aa < b.aa ? -1 : a.aa > b.aa ? 1 : a.codon < b.codon ? -1 : 1,
  );
  return { total, stats };
}
